import React, { useState, useEffect } from 'react';
import { ArrowRight, Phone, CheckCircle2, Sparkles, Smartphone, Code2, TrendingUp, Cpu, ChevronRight } from 'lucide-react';
import { companyInfo } from '../../data/companyInfo';

interface HeroSectionProps {
  onExploreServices: () => void;
  onOpenConsultation: () => void;
  onStartProject?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ 
  onExploreServices, 
  onOpenConsultation, 
  onStartProject 
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const serviceShowcase = [
    {
      id: 'ai-web',
      name: 'AI Web Development',
      badge: 'Intelligent Web Architecture',
      image: '/assets/portfolio-web-hd.jpg',
      icon: Code2,
      accentColor: '#7B2EFF',
      borderColor: 'border-[#7B2EFF]/50',
      glowColor: 'shadow-glow-purple',
      textColor: 'text-[#7B2EFF]',
      expectation: 'Autonomous personalization, real-time vector search & 100% Core Web Vitals.',
      vision: 'Transforming static websites into intelligent 24/7 conversion engines tailored to visitor intent.',
      metrics: '+240% Lead Retention • 0.4s Global Load Time',
      badgeLabel: 'AI Engine Active',
    },
    {
      id: 'mobile-apps',
      name: 'Mobile Apps',
      badge: 'Hybrid & Cross-Platform',
      image: '/assets/portfolio-mobile-hd.jpg',
      icon: Smartphone,
      accentColor: '#159DE8',
      borderColor: 'border-[#159DE8]/50',
      glowColor: 'shadow-glow-blue',
      textColor: 'text-[#159DE8]',
      expectation: 'Fluid 60-120fps native performance on iOS & Android with single-codebase efficiency.',
      vision: 'Delivering tactile, high-retention mobile experiences with instant biometrics and offline sync.',
      metrics: '95% Code Sharing • 4.9★ App Store Rating',
      badgeLabel: 'iOS & Android Sync',
    },
    {
      id: 'digital-marketing',
      name: 'Digital Marketing & SEO',
      badge: 'Performance & Revenue Growth',
      image: '/assets/portfolio-marketing-hd.jpg',
      icon: TrendingUp,
      accentColor: '#FF851B',
      borderColor: 'border-[#FF851B]/50',
      glowColor: 'shadow-glow-orange',
      textColor: 'text-[#FF851B]',
      expectation: 'High-ROI performance marketing, technical SEO dominance & full attribution.',
      vision: 'Scaling qualified customer acquisition curves and driving sustainable commercial pipeline velocity.',
      metrics: '+340% Pipeline Velocity • -42% CAC',
      badgeLabel: 'Platform Growth: +145%',
    },
    {
      id: 'custom-solutions',
      name: 'Custom ERP & Software',
      badge: 'Enterprise Workflows',
      image: '/assets/portfolio-business-hd.jpg',
      icon: Cpu,
      accentColor: '#00C853',
      borderColor: 'border-[#00C853]/50',
      glowColor: 'shadow-md',
      textColor: 'text-[#00C853]',
      expectation: 'Unified operations, automated accounting reconciliations & 100% IP ownership.',
      vision: 'Replacing brittle manual spreadsheets with resilient cloud-native operational software.',
      metrics: '99.99% Cloud SLA • Zero Data Drift',
      badgeLabel: 'Microservices Healthy',
    },
  ];

  // Auto-cycle through services every 6 seconds if user is idle
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % serviceShowcase.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [serviceShowcase.length]);

  const current = serviceShowcase[activeTab];

  return (
    <section className="relative w-full bg-[#071426] overflow-hidden pt-8 pb-12 lg:pt-14 lg:pb-16 border-b border-[#1b2f4a]/50">
      {/* Background ambient lighting */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#159DE8]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-10 right-10 w-[550px] h-[550px] bg-[#0F6DFF]/15 rounded-full blur-[110px] pointer-events-none" />
      <div className="absolute bottom-5 left-1/3 w-80 h-80 bg-[#FF851B]/8 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: ~52% */}
          <div className="lg:col-span-7 space-y-6 text-left">
            
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342]/80 border border-[#1b2f4a] text-xs font-semibold tracking-wider text-[#FF851B] uppercase shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#FF851B] animate-pulse" />
              <span>{companyInfo.eyebrow}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-white leading-[1.14] tracking-tight font-display">
              {companyInfo.heroHeadingLine1} <br className="hidden sm:inline" />
              <span className="text-[#FF851B]">Powerful Digital</span>{' '}
              <span className="text-[#159DE8]">Solutions</span>
            </h1>

            {/* Supporting Description */}
            <p className="text-[#D5DFEC] text-base sm:text-lg leading-relaxed max-w-2xl font-normal">
              {companyInfo.heroDescription}
            </p>

            {/* Clean, Focused Dual CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2">
              <button
                onClick={onStartProject || onOpenConsultation}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm sm:text-base font-bold shadow-lg hover:shadow-glow-orange hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              >
                <Sparkles size={17} className="fill-white/20" />
                <span>Start Your Project</span>
                <ArrowRight size={16} />
              </button>

              <button
                onClick={onExploreServices}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] hover:border-[#159DE8] text-white text-sm sm:text-base font-semibold hover:bg-[#102a4e] active:scale-[0.98] transition-all duration-200"
              >
                <span>Explore Services</span>
              </button>
            </div>

            {/* Approved Trust Message & Key Value Bullets */}
            <div className="pt-3 space-y-3">
              <p className="text-xs sm:text-sm text-[#FF851B] font-medium flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF851B]" />
                <span>{companyInfo.heroTrustMessage}</span>
              </p>

              <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-xs text-[#8FA0B8] border-t border-[#1b2f4a]/50 pt-3">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-[#00C853]" />
                  <span>Serving USA, UK & Canada</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-[#00C853]" />
                  <span>Quality Website Engineering</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-[#00C853]" />
                  <span>Continuous Long-Term Website Care</span>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: ~48% - Upgraded High-End 3D Visual & Interactive Service Showcase */}
          <div className="lg:col-span-5 relative flex flex-col items-center">
            
            {/* Ambient blue backdrop glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#159DE8]/15 via-[#7B2EFF]/10 to-[#FF851B]/10 rounded-3xl blur-2xl transform scale-95" />
            
            {/* Outer Showcase Container */}
            <div className="relative w-full rounded-2xl overflow-hidden border border-[#1b2f4a] bg-[#050A13] shadow-2xl group transition-all duration-300">
              
              {/* Interactive Service Filter Tabs on Top of the Banner */}
              <div className="p-2 bg-[#071426]/90 backdrop-blur-md border-b border-[#1b2f4a] flex items-center justify-between gap-1 overflow-x-auto scrollbar-none">
                {serviceShowcase.map((s, idx) => {
                  const Icon = s.icon;
                  const isActive = activeTab === idx;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setActiveTab(idx)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                        isActive
                          ? 'bg-[#0B2342] text-white border border-[#159DE8]/50 shadow-sm'
                          : 'text-[#8FA0B8] hover:text-white hover:bg-white/5'
                      }`}
                      aria-label={`Showcase ${s.name}`}
                    >
                      <Icon size={13} className={isActive ? s.textColor : 'text-[#8FA0B8]'} />
                      <span className="hidden sm:inline">{s.name.split(' ')[0]}</span>
                      <span className="sm:hidden">{s.name.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </div>

              {/* Main Dynamic Showcase Artwork */}
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#050A13]">
                <img
                  key={current.id}
                  src={current.image || '/assets/portfolio-web-hd.jpg'}
                  alt={`EBON Media Solutions - ${current.name}`}
                  className="w-full h-full object-cover block group-hover:scale-[1.02] transition-all duration-700 animate-fade-in"
                  loading="eager"
                  decoding="async"
                />
                
                {/* Subtle vignette gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A13] via-transparent to-transparent opacity-70 pointer-events-none" />

                {/* Live Holographic Badge Top-Right */}
                <div className="absolute top-3 right-3 bg-[#071426]/90 backdrop-blur-md border border-[#1b2f4a] rounded-lg px-2.5 py-1 shadow-lg flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#00C853] animate-ping" />
                  <span className="text-[10px] font-mono font-bold text-white tracking-wide">
                    {current.badgeLabel}
                  </span>
                </div>
              </div>

              {/* Active Service Vision & Customer Expectation Drawer */}
              <div className="p-4 bg-[#071426] border-t border-[#1b2f4a] space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-2.5 h-2.5 rounded-full" 
                      style={{ backgroundColor: current.accentColor }} 
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-white">
                      {current.name}
                    </span>
                    <span className="text-[10px] text-[#8FA0B8] px-2 py-0.5 rounded bg-[#050A13] border border-[#1b2f4a]">
                      {current.badge}
                    </span>
                  </div>

                  <span className="text-[11px] font-mono font-bold text-[#159DE8]">
                    {current.metrics}
                  </span>
                </div>

                {/* Customer Expectation & Vision Text */}
                <p className="text-xs text-[#D5DFEC] leading-relaxed">
                  <strong className="text-white">Customer Vision:</strong> {current.vision}
                </p>

                <div className="pt-2 flex items-center justify-between border-t border-[#1b2f4a]/60 text-[11px]">
                  <span className="text-[#8FA0B8]">
                    <strong className="text-white">Expectation:</strong> {current.expectation}
                  </span>
                  
                  <button
                    onClick={onExploreServices}
                    className="inline-flex items-center gap-1 text-[#FF851B] hover:text-white font-semibold shrink-0 ml-2 transition-colors"
                  >
                    <span>View Specs</span>
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>

            </div>

          </div>

        </div>
      </div>
    </section>
  );
};
